/**
 * Redux slice for GIG shift group actions.
 *
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GigShiftDay, GigShiftGroup } from '../../types/gigShiftTypes';

export type GigShiftDecision = 'approve' | 'reject';

export interface DecideGigGroupPayload {
    requestId: number | string;
    decision: GigShiftDecision;
    decidedById?: string;
    decidedByName?: string;
    rejectionNotes?: string;
}

export interface UpdateGigGroupPayload {
    requestId: number | string;
    updatedTitle?: string;
    updatedNotes?: string;
    updatedDays: GigShiftDay[];
    updatedById?: string;
}

interface GigShiftState {
    lastDecision: DecideGigGroupPayload | null;
    lastUpdate: UpdateGigGroupPayload | null;
    loading: boolean;
    success: boolean;
    error: string | null;
}

const initialState: GigShiftState = {
    lastDecision: null,
    lastUpdate: null,
    loading: false,
    success: false,
    error: null,
};

export const decideGigGroup = createAsyncThunk(
    'gigShift/decideGigGroup',
    async (payload: DecideGigGroupPayload, { rejectWithValue }) => {
        try {
            console.log('🟡 [stub] decideGigGroup payload:', payload);
            await new Promise((resolve) => setTimeout(resolve, 250));
            return payload;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to decide GIG shift');
        }
    }
);

export const updateGigGroup = createAsyncThunk(
    'gigShift/updateGigGroup',
    async (payload: UpdateGigGroupPayload, { rejectWithValue }) => {
        try {
            console.log('🟡 [stub] updateGigGroup payload:', payload);
            await new Promise((resolve) => setTimeout(resolve, 250));
            return payload;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to update GIG shift');
        }
    }
);

/** Build a decide payload from a group + a decision. */
export const buildDecidePayload = (
    group: GigShiftGroup,
    decision: GigShiftDecision,
    decidedById?: string,
    decidedByName?: string,
    rejectionNotes?: string,
): DecideGigGroupPayload => ({
    requestId: group.requestId,
    decision,
    decidedById,
    decidedByName,
    rejectionNotes,
});

const gigShiftSlice = createSlice({
    name: 'gigShift',
    initialState,
    reducers: {
        resetGigShiftState: (state) => {
            state.lastDecision = null;
            state.lastUpdate = null;
            state.loading = false;
            state.success = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(decideGigGroup.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
            })
            .addCase(decideGigGroup.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.lastDecision = action.payload;
            })
            .addCase(decideGigGroup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateGigGroup.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
            })
            .addCase(updateGigGroup.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.lastUpdate = action.payload;
            })
            .addCase(updateGigGroup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { resetGigShiftState } = gigShiftSlice.actions;
export default gigShiftSlice.reducer;
